// Dashboard realtime: ONE Socket.IO `'data'` listener wired to the RTK Query
// cache (§7 "React plan"). FAITHFUL re-platform of the Backbone
// `projects.js` `onSocketData` reactions.
//
// Backbone (`view/projects.js` lines 82-108) reactions for model 'ksdmproject'
// (mapped via models.mapping.js → 'model.project'):
//   - code 'post'  && id NOT in collection  → collection.fetch().then(render)
//   - code 'delete' && id IN collection      → collection.remove(id)
//   - code 'put'    && id IN collection      → model.fetch().then(render)
//   - update_project_notification_data       → 10s-DEBOUNCED model.fetch().then(render)
// The author's comment on 'post' ("not right … appeared twice when user created
// project") is WHY we refetch instead of inserting locally — replicating that
// avoids the duplicate-row bug. We mirror it: refetch-then-render via RTK Query
// (invalidation triggers a refetch which re-renders), and use `updateQueryData`
// for the in-place delete (matching `collection.remove`).
//
// Unread `new_project` highlight (parity 78 / §3.A): the Backbone row reads the
// header's unread `new_project` notifications to add `.unread-notification`. The
// React seam is `dashboardSlice.unreadProjectIds`. On a project syncdata we
// refresh the header notifications and recompute the unread project ids so a
// brand new project's highlight appears (cleared on click in 2c).

import { useEffect, useMemo, useRef } from 'react';
import { useAppDispatch } from 'app/hooks';
import { dashboardApi } from 'app/services/dashboardApi';
import { headerApi } from 'app/services/headerApi';
import { setUnreadProjects } from 'app/slices/dashboardSlice';
import { store } from 'app/store';
import { getSocket, type SocketDataMessage } from 'app/socketClient';
import type { RootState } from 'app/store';
import type { IProjectsPage, IProjectNotification } from 'models';

const { useGetProjectNotificationsQuery } = headerApi;

// Dashboard-relevant slice of models.mapping.js: only 'ksdmproject' matters for
// the project list (model.project === the dashboard project model).
const PROJECT_DJANGO_MODEL = 'ksdmproject';

const NOTIF_DEBOUNCE_MS = 10000;

/** True if `id` is present in ANY cached getProjects / getPinnedProjects page. */
function isProjectInCache(state: RootState, id: number): boolean {
  const mainArgs = dashboardApi.util.selectCachedArgsForQuery(state, 'getProjects');
  for (const args of mainArgs) {
    const page: IProjectsPage | undefined =
      dashboardApi.endpoints.getProjects.select(args)(state).data;
    if (page?.rows.some((r) => r.id === id)) return true;
  }
  const pinnedArgs = dashboardApi.util.selectCachedArgsForQuery(state, 'getPinnedProjects');
  for (const args of pinnedArgs) {
    const page: IProjectsPage | undefined =
      dashboardApi.endpoints.getPinnedProjects.select(args)(state).data;
    if (page?.rows.some((r) => r.id === id)) return true;
  }
  return false;
}

/** Collect unread `new_project` project ids from cached header notifications. */
function unreadNewProjectIds(state: RootState): number[] {
  const ids = new Set<number>();
  const argsList = headerApi.util.selectCachedArgsForQuery(state, 'getProjectNotifications');
  argsList.forEach((page) => {
    const data = headerApi.endpoints.getProjectNotifications.select(page)(state).data;
    data?.rows.forEach((n: IProjectNotification) => {
      if (!n.is_read && !n.is_critical_task_started && n.project_id != null) {
        ids.add(n.project_id);
      }
    });
  });
  return Array.from(ids);
}

/**
 * Mount-once realtime listener. Register from a single, always-mounted point on
 * the dashboard (DashboardPage) so EXACTLY ONE `'data'` handler is attached.
 */
export function useProjectSocket(): void {
  const dispatch = useAppDispatch();
  // Single 10s debounce timer for update_project_notification_data (parity:
  // cleared + restarted on each event).
  const notifTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const socket = getSocket();

    const onData = (msg: SocketDataMessage) => {
      if (!msg || msg.model !== PROJECT_DJANGO_MODEL) return;
      const id = msg.data?.id ?? msg.id;
      if (id == null) return;
      const state = store.getState();

      if (msg.code === 'post' && !isProjectInCache(state, id)) {
        // New project not yet in any loaded page → refetch the list (refetch-
        // then-render). Invalidating Projects re-runs page 1; the data layer
        // replaces the accumulator on the page-1 result (no duplicate row).
        dispatch(dashboardApi.util.invalidateTags(['Projects', 'PinnedProjects']));
        // A new project arrives with a `new_project` notification: refresh the
        // header notifications so the unread badge/highlight populates.
        dispatch(headerApi.util.invalidateTags([{ type: 'Notifications', id: 1 }]));
      } else if (msg.code === 'delete' && isProjectInCache(state, id)) {
        // In-place removal across every cached page (≈ collection.remove).
        dashboardApi.util
          .selectCachedArgsForQuery(state, 'getProjects')
          .forEach((args) =>
            dispatch(
              dashboardApi.util.updateQueryData('getProjects', args, (draft) => {
                const i = draft.rows.findIndex((r) => r.id === id);
                if (i !== -1) draft.rows.splice(i, 1);
              })
            )
          );
        dashboardApi.util
          .selectCachedArgsForQuery(state, 'getPinnedProjects')
          .forEach((args) =>
            dispatch(
              dashboardApi.util.updateQueryData('getPinnedProjects', args, (draft) => {
                const i = draft.rows.findIndex((r) => r.id === id);
                if (i !== -1) draft.rows.splice(i, 1);
              })
            )
          );
      } else if (msg.code === 'put' && isProjectInCache(state, id)) {
        // Single-project update → refetch + re-render. No single-row GET exists
        // here, so invalidate the list tags (the faithful "refetch-then-render";
        // refetch re-renders, avoiding any local-insert duplication).
        dispatch(dashboardApi.util.invalidateTags(['Projects', 'PinnedProjects']));
      }

      // update_project_notification_data → 10s-DEBOUNCED single-project refetch.
      if (msg.update_project_notification_data) {
        if (notifTimer.current) clearTimeout(notifTimer.current);
        notifTimer.current = setTimeout(() => {
          dispatch(dashboardApi.util.invalidateTags(['Projects', 'PinnedProjects']));
          dispatch(headerApi.util.invalidateTags([{ type: 'Notifications', id: 1 }]));
          dispatch(setUnreadProjects(unreadNewProjectIds(store.getState())));
          notifTimer.current = null;
        }, NOTIF_DEBOUNCE_MS);
      }
    };

    // EXACTLY ONE `'data'` listener (the legacy leaks; we don't).
    socket.on('data', onData);

    return () => {
      socket.off('data', onData);
      if (notifTimer.current) {
        clearTimeout(notifTimer.current);
        notifTimer.current = null;
      }
    };
  }, [dispatch]);
}

/**
 * Keep `dashboardSlice.unreadProjectIds` populated from the header's project
 * notifications (parity 78 / §3.A): the project ids of UNREAD `new_project`
 * (non-critical-task) notifications drive `.unread-notification` on the matching
 * rows (cleared on click in 2c). This subscribes to notifications page 1 (the
 * same query the NotificationsDropdown uses, so it shares the cache) and pushes
 * the derived ids into the slice whenever they change.
 */
export function useUnreadProjectsSync(): void {
  const dispatch = useAppDispatch();
  const { data } = useGetProjectNotificationsQuery(1);

  const ids = useMemo(() => {
    const set = new Set<number>();
    data?.rows.forEach((n) => {
      if (!n.is_read && !n.is_critical_task_started && n.project_id != null) {
        set.add(n.project_id);
      }
    });
    return Array.from(set).sort((a, b) => a - b);
  }, [data]);

  // Stable key so we only dispatch when the id SET actually changes (avoids a
  // dispatch loop; clearUnreadProject in 2c is a one-way removal we don't undo
  // here unless the underlying notification set genuinely changes).
  const sig = ids.join(',');
  const prevSig = useRef<string | null>(null);

  useEffect(() => {
    if (prevSig.current === sig) return;
    prevSig.current = sig;
    dispatch(setUnreadProjects(ids));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig, dispatch]);
}
